import React, { useCallback, useMemo, useRef, useState } from 'react'
import moment from 'moment'
import { SectionList, View } from 'react-native'
import { useFocusEffect } from '@react-navigation/native'
import { useDispatch, useSelector } from 'react-redux'
import { Analytics, CustomEventTypes } from '@src/config'
import { Button, Text } from '@src/components/base'
import { RefreshControlComponent as CommonRefreshControl } from '@src/components/RefreshControl'
import { InboxBanner } from '@src/components/notifications/banners/InboxBanner'
import { NotificationRecord } from '@src/models/notifications/types'
import { StyleService, useStyleSheet } from '@src/style/service'
import { useDispatchAsync } from '@src/utils'
import { handleEngineNotification } from '@src/components/notifications/utils/handler'
import { userSelector } from '@src/selectors/app'
import {
  notificationInboxUnreadCountSelector,
  notificationInboxNextCursorSelector,
  notificationInboxRecordsSelector,
} from '@src/selectors/notifications'
import { NotificationInboxStateFilter } from '@src/types'
import {
  NotificationInboxFooterLoader,
  NotificationInboxLoadingContent,
} from './NotificationInboxLoadingContent'
import { NotificationInboxRow } from './NotificationInboxRow'
import { useAutoMarkNoneActionNotificationsRead } from './hooks/useAutoMarkNoneActionNotificationsRead'
import { useMarkAllInboxNotificationsRead } from './hooks/useMarkAllInboxNotificationsRead'
import {
  formatNotificationTimestamp,
  NotificationInboxSection,
  groupNotificationsByDate,
} from './utils'
import { NotificationInboxContainer } from './NotificationInboxContainer'

const INBOX_STATE_FILTER = NotificationInboxStateFilter.All

export const NotificationInboxScreen = () => {
  const styles = useStyleSheet(themedStyles)
  const dispatch = useDispatch()
  const dispatchAsync = useDispatchAsync()
  const user = useSelector(userSelector)
  const recordsSelector = useMemo(() => notificationInboxRecordsSelector(INBOX_STATE_FILTER), [])
  const nextCursorSelector = useMemo(
    () => notificationInboxNextCursorSelector(INBOX_STATE_FILTER),
    [],
  )
  const notifications = useSelector(recordsSelector)
  const unreadCount = useSelector(notificationInboxUnreadCountSelector)
  const nextCursor = useSelector(nextCursorSelector)
  const isLoadingRef = useRef(false)
  const [loading, setLoading] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Read the cursor from a ref so fetchInbox stays referentially stable. Putting
  // nextCursor in fetchInbox's deps would recreate it on every pagination, which in turn
  // would re-run the useFocusEffect below and refetch (page 1 again), wiping the appended page.
  //
  const nextCursorRef = useRef(nextCursor)
  nextCursorRef.current = nextCursor

  // Read the unread count from a ref so the focus effect below can report it without adding
  // unreadCount to its deps (which would re-run the effect — and refetch — on every count change).
  const unreadCountRef = useRef(unreadCount)
  unreadCountRef.current = unreadCount

  const fetchInbox = useCallback(
    async ({ append = false, refresh = false }: { append?: boolean; refresh?: boolean } = {}) => {
      if (isLoadingRef.current) {
        return
      }

      const after = nextCursorRef.current

      if (append && !after) {
        return
      }

      isLoadingRef.current = true
      setErrorMessage(null)
      setLoading(!append && !refresh)
      setRefreshing(refresh)
      setLoadingMore(append)

      try {
        await dispatchAsync({
          type: append ? 'notifications/fetchInboxNextPage' : 'notifications/fetchInbox',
          payload: append
            ? { after, stateFilter: INBOX_STATE_FILTER }
            : { stateFilter: INBOX_STATE_FILTER },
        })
      } catch (error: any) {
        setErrorMessage(error?.message || 'We could not load your notifications right now.')
      } finally {
        isLoadingRef.current = false
        setLoading(false)
        setRefreshing(false)
        setLoadingMore(false)
      }
    },
    [dispatchAsync],
  )

  useFocusEffect(
    useCallback(() => {
      Analytics.track(CustomEventTypes.NotificationInboxViewed, {
        unreadCount: unreadCountRef.current,
      })
      fetchInbox()
    }, [fetchInbox]),
  )

  const handleOpenNotification = useCallback(
    (notification: NotificationRecord) => {
      Analytics.track(CustomEventTypes.NotificationInboxNotificationTapped, {
        id: notification.id,
        actionKind: notification.actionKind,
        kind: notification.kind,
        userId: user?.id,
      })

      handleEngineNotification({
        notificationData: {
          id: notification.id,
          actionKind: notification.actionKind,
          kind: notification.kind,
          ...(notification.payload || {}),
        },
        userId: user?.id,
        dispatch,
        completeOnInteraction: false,
        markReadOnInteraction: true,
      })
    },
    [dispatch, user?.id],
  )

  useAutoMarkNoneActionNotificationsRead({
    notifications,
    // Delay until initial fetch settles to avoid racing stale unread counts.
    enabled: !loading && !refreshing,
    delayMs: 350,
  })

  const { markAllAsReadLoading, onMarkAllAsReadPress } = useMarkAllInboxNotificationsRead({
    unreadCount,
    stateFilter: INBOX_STATE_FILTER,
  })

  const { now, sections } = useMemo<{
    now: moment.Moment
    sections: NotificationInboxSection[]
  }>(() => {
    const referenceNow = moment()

    return {
      now: referenceNow,
      sections: groupNotificationsByDate(notifications, referenceNow),
    }
  }, [notifications])

  const renderNotification = useCallback(
    ({
      item,
      index,
      section,
    }: {
      item: NotificationRecord
      index: number
      section: NotificationInboxSection
    }) => {
      return (
        <NotificationInboxRow
          isFirst={index === 0}
          isLast={index === section.data.length - 1}
          item={item}
          onPress={handleOpenNotification}
          timestamp={formatNotificationTimestamp(item.createdAt, now)}
        />
      )
    },
    [handleOpenNotification, now],
  )

  const renderSectionHeader = useCallback(
    ({ section }: { section: NotificationInboxSection }) => {
      if (sections.length <= 1) {
        return null
      }

      const isFirstSection = section.key === sections[0]?.key

      return (
        <View style={[styles.sectionHeaderContainer, isFirstSection && styles.sectionHeaderFirst]}>
          <Text type="small" bold style={styles.sectionHeaderText}>
            {section.title.toUpperCase()}
          </Text>
        </View>
      )
    },
    [sections, styles.sectionHeaderContainer, styles.sectionHeaderFirst, styles.sectionHeaderText],
  )

  const handleLoadMore = useCallback(() => {
    Analytics.track(CustomEventTypes.NotificationInboxLoadMoreTapped)
    fetchInbox({ append: true })
  }, [fetchInbox])

  const renderFooter = useMemo(() => {
    if (loadingMore) {
      return <NotificationInboxFooterLoader />
    }

    if (!nextCursor) {
      return <View style={styles.footerSpacing} />
    }

    return (
      <View style={styles.footer}>
        <Button
          accessibilityLabel="Load more notifications"
          onPress={handleLoadMore}
          size="block"
          type="transparent"
        >
          <Text type="small">Load More</Text>
        </Button>
      </View>
    )
  }, [handleLoadMore, loadingMore, nextCursor, styles.footer, styles.footerSpacing])

  const handleMarkAllAsRead = useCallback(() => {
    setErrorMessage(null)
    onMarkAllAsReadPress()
  }, [onMarkAllAsReadPress])

  if ((loading && notifications.length === 0) || markAllAsReadLoading) {
    return (
      <NotificationInboxContainer
        onMarkAllAsReadPress={handleMarkAllAsRead}
        loading={markAllAsReadLoading}
      >
        <NotificationInboxLoadingContent />
      </NotificationInboxContainer>
    )
  }

  if (errorMessage && notifications.length === 0) {
    return (
      <NotificationInboxContainer
        onMarkAllAsReadPress={handleMarkAllAsRead}
        loading={markAllAsReadLoading}
      >
        <View style={styles.centeredState}>
          <Text type="regular" style={styles.centeredText}>
            {errorMessage}
          </Text>
          <Button
            accessibilityLabel="Retry loading notifications"
            onPress={() => fetchInbox()}
            size="small"
            type="primary"
          >
            Retry
          </Button>
        </View>
      </NotificationInboxContainer>
    )
  }

  return (
    <NotificationInboxContainer
      onMarkAllAsReadPress={handleMarkAllAsRead}
      loading={markAllAsReadLoading}
    >
      <SectionList
        contentContainerStyle={
          sections.length === 0 ? styles.emptyContentContainer : styles.listContentContainer
        }
        keyExtractor={(item) => item.id}
        ListEmptyComponent={
          <View style={styles.centeredState}>
            <Text type="title-3" style={styles.centeredText}>
              No notifications yet
            </Text>
            <Text type="regular" style={styles.centeredText}>
              Your inbox history will appear here when notifications become available.
            </Text>
          </View>
        }
        ListHeaderComponent={<InboxBanner />}
        ListFooterComponent={renderFooter}
        refreshControl={
          <CommonRefreshControl
            refreshing={refreshing}
            onRefresh={() => fetchInbox({ refresh: true })}
          />
        }
        renderItem={renderNotification}
        renderSectionHeader={renderSectionHeader}
        sections={sections}
        showsVerticalScrollIndicator={false}
        stickySectionHeadersEnabled={false}
      />
    </NotificationInboxContainer>
  )
}

const themedStyles = StyleService.create({
  listContentContainer: {
    padding: 16,
  },
  emptyContentContainer: {
    flexGrow: 1,
    padding: 16,
  },
  centeredState: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    paddingHorizontal: 24,
  },
  centeredText: {
    marginBottom: 12,
    textAlign: 'center',
  },
  sectionHeaderContainer: {
    marginBottom: 10,
    marginTop: 20,
  },
  sectionHeaderFirst: {
    marginTop: 0,
  },
  sectionHeaderText: {
    color: 'theme.text.secondary',
  },
  footer: {
    alignItems: 'center',
    paddingTop: 16,
    paddingBottom: 16,
  },
  footerSpacing: {
    height: 24,
  },
})
